import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Users, Building2, Send, CheckCircle2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Seo from "../components/Seo";

const SPACES = [
  {
    name: "Main Sanctuary",
    capacity: "Up to 200",
    image: "/images/facility-1.jpg",
    description: "Our worship space — ideal for services, weddings, funerals, conferences, and large gatherings.",
  },
  {
    name: "Fellowship Hall",
    capacity: "Up to 80",
    image: "/images/facility-3.jpg",
    description: "Multipurpose room for receptions, baby showers, repast meals, small conferences, and community events.",
  },
];

const GALLERY = [
  "/images/facility-1.jpg",
  "/images/facility-2.jpg",
  "/images/facility-3.jpg",
  "/images/facility-4.jpg",
  "/images/facility-5.jpg",
  "/images/facility-6.jpg",
  "/images/facility-7.jpg",
  "/images/facility-8.jpg",
  "/images/facility-9.jpg",
  "/images/facility-10.jpg",
];

export default function Booking() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    space: "Main Sanctuary",
    event_name: "", event_description: "",
    event_date: "", start_time: "", end_time: "",
    expected_attendance: 50,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, expected_attendance: Number(form.expected_attendance) || 1 };
      if (!payload.phone) delete payload.phone;
      await api.post("/bookings", payload);
      setDone(true);
      toast.success("Request submitted! We'll be in touch soon.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main id="main-content" className="bg-[#050505] text-white min-h-[70vh] flex items-center" data-testid="booking-success-page">
        <Seo title="Request Submitted" path="/booking" />
        <div className="max-w-2xl mx-auto px-5 lg:px-10 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto mb-6 ring-1 ring-electric/40">
            <CheckCircle2 className="w-10 h-10 text-electric" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">Request Received</div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-5">
            Thank you. We'll be in <span className="italic text-electric">touch soon.</span>
          </h1>
          <p className="text-zinc-300 leading-relaxed mb-8">
            Your booking request for <strong className="text-white">{form.space}</strong> on <strong className="text-white">{form.event_date}</strong> has been received. The Push Center team will review it within 48 hours and email you a decision.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold hover:bg-[#4ade80] transition" data-testid="booking-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#050505] text-white" data-testid="booking-page">
      <Seo
        title="Book A Space"
        description="Reserve the Main Sanctuary or Fellowship Hall at The Push Center, Portsmouth VA. Weddings, funerals, conferences, receptions, and more."
        path="/booking"
      />

      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/facility-1.jpg" alt="" className="w-full h-full object-cover opacity-70 bg-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
          <div className="absolute inset-0 lightning-grid opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Book A Space
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            Host your event <span className="italic text-electric">with us.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl">
            Reserve the Main Sanctuary or Fellowship Hall for weddings, funerals, conferences, receptions, and community gatherings.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-16">
        <div className="grid sm:grid-cols-2 gap-5">
          {SPACES.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-2xl overflow-hidden border border-white/5 bg-[#0a0a0a] group"
              data-testid={`space-card-${i}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-7">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-electric">{s.capacity}</div>
                  <Building2 className="w-5 h-5 text-electric" />
                </div>
                <h3 className="font-display font-black text-2xl mb-2">{s.name}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
        <div className="text-[10px] uppercase tracking-[0.3em] text-electric font-semibold mb-6">Gallery</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {GALLERY.map((src, i) => (
            <div key={src} className="aspect-square overflow-hidden rounded-lg" data-testid={`gallery-${i}`}>
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* FORM */}
      <section id="request" className="max-w-4xl mx-auto px-5 lg:px-10 pb-32">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
          Request to Book
        </div>
        <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-3">
          Submit your <span className="italic text-electric">request.</span>
        </h2>
        <p className="text-zinc-400 mb-10">
          Fill out the form below. We'll review your request and email you a decision within 48 hours.
        </p>

        <form onSubmit={submit} className="space-y-6 p-8 lg:p-10 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="booking-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your Name">
              <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" />
            </Field>
            <Field label="Email">
              <input type="email" className={inputCls} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Phone (optional)">
              <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" />
            </Field>
            <Field label="Space">
              <select className={inputCls} value={form.space} onChange={(e) => setForm({ ...form, space: e.target.value })} data-testid="booking-space">
                {SPACES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Event Name">
            <input className={inputCls} required value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} placeholder="e.g., Smith Family Wedding" data-testid="booking-event-name" />
          </Field>

          <div className="grid sm:grid-cols-3 gap-5">
            <Field label={<><Calendar className="w-3 h-3 inline mr-1.5" />Date</>}>
              <input type="date" className={inputCls} required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="booking-date" />
            </Field>
            <Field label={<><Clock className="w-3 h-3 inline mr-1.5" />Start Time</>}>
              <input type="time" className={inputCls} required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="booking-start" />
            </Field>
            <Field label={<><Clock className="w-3 h-3 inline mr-1.5" />End Time</>}>
              <input type="time" className={inputCls} required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="booking-end" />
            </Field>
          </div>

          <Field label={<><Users className="w-3 h-3 inline mr-1.5" />Expected Attendance</>}>
            <input type="number" min="1" max="2000" className={inputCls} required value={form.expected_attendance} onChange={(e) => setForm({ ...form, expected_attendance: e.target.value })} data-testid="booking-attendance" />
          </Field>

          <Field label="Event Description">
            <textarea rows={5} className={inputCls} required value={form.event_description} onChange={(e) => setForm({ ...form, event_description: e.target.value })} placeholder="Tell us about your event — purpose, format, any setup needs..." data-testid="booking-description" />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
            data-testid="booking-submit"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </form>
      </section>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">{label}</label>
      {children}
    </div>
  );
}
